import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import { DEPARTMENTS, ROLES } from '../../utils/constants';
import api from '../../services/api';

const EmployeeEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [allEmployees, setAllEmployees] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    employee_code: '',
    departments: [],
    role: '',
    managers: [],
    reporting_manager: '',
    working_hours: {
      check_in_time: '09:00',
      check_out_time: '18:00',
      timezone: 'Asia/Kolkata'
    },
    is_active: true
  });

  useEffect(() => {
    fetchEmployee();
    fetchAllEmployees();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEmployee = async () => {
    try {
      const response = await api.get(`/employees/${id}`);
      const emp = response.data.data;
      setFormData({
        name: emp.name,
        email: emp.email,
        phone: emp.phone,
        employee_code: emp.employee_code,
        departments: emp.departments || [emp.department],
        role: emp.role,
        managers: emp.managers?.map(m => m._id || m) || [],
        reporting_manager: emp.reporting_manager?._id || emp.reporting_manager || '',
        working_hours: emp.working_hours || {
          check_in_time: '09:00',
          check_out_time: '18:00',
          timezone: 'Asia/Kolkata'
        },
        is_active: emp.is_active
      });
    } catch (error) {
      alert('Failed to load employee');
      navigate('/admin/employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const response = await api.get('/employees', { params: { limit: 1000 } });
      const employees = response.data.data?.employees || [];
      setAllEmployees(Array.isArray(employees) ? employees : []);
    } catch (error) {
      console.error('Failed to load employees for manager selection:', error);
      setAllEmployees([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'departments' || name === 'managers') {
      // Handle multi-select for departments and managers
      const options = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({
        ...formData,
        [name]: options
      });
    } else if (name.startsWith('working_hours.')) {
      // Handle nested working_hours fields
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        working_hours: {
          ...formData.working_hours,
          [field]: value
        }
      });
    } else {
      const val = type === 'checkbox' ? checked : value;
      setFormData({
        ...formData,
        [name]: val
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError('');

    try {
      await api.patch(`/employees/${id}`, formData);
      navigate('/admin/employees');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update employee');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid" style={{ maxWidth: '800px' }}>
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/admin/employees')}>
          <i className="ti ti-arrow-left me-1" />
          Back to Employee Management
        </button>

        <div className="card">
          <div className="card-body">
            <h2 className="fs-5 mb-3">Edit Employee</h2>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Employee Code *</label>
                <input
                  type="text"
                  name="employee_code"
                  className="form-control"
                  value={formData.employee_code}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Departments * (Hold Ctrl/Cmd to select multiple)</label>
                <select
                  name="departments"
                  className="form-select"
                  value={formData.departments}
                  onChange={handleChange}
                  multiple
                  required
                  style={{ minHeight: '120px' }}
                >
                  {Object.values(DEPARTMENTS).map((dept) => (
                    <option key={dept} value={dept}>
                      {dept.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Selected: {formData.departments.length > 0 ? formData.departments.map(d => d.replace(/_/g, ' ')).join(', ') : 'None'}
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Role *</label>
                <select
                  name="role"
                  className="form-select"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Role</option>
                  {Object.values(ROLES).map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Managers (Hold Ctrl/Cmd to select multiple)</label>
                <select
                  name="managers"
                  className="form-select"
                  value={formData.managers}
                  onChange={handleChange}
                  multiple
                  style={{ minHeight: '120px' }}
                >
                  {Array.isArray(allEmployees) && allEmployees
                    .filter(emp => emp._id !== id)
                    .map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name} ({emp.employee_code}) - {emp.role}
                      </option>
                    ))}
                </select>
                <small className="form-text text-muted">
                  Selected: {formData.managers.length > 0
                    ? (Array.isArray(allEmployees) ? allEmployees.filter(emp => formData.managers.includes(emp._id)).map(emp => emp.name).join(', ') : 'Loading...')
                    : 'None'}
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Reporting Manager</label>
                <select
                  name="reporting_manager"
                  className="form-select"
                  value={formData.reporting_manager}
                  onChange={handleChange}
                >
                  <option value="">No Reporting Manager</option>
                  {Array.isArray(allEmployees) && allEmployees
                    .filter(emp => emp._id !== id)
                    .map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name} ({emp.employee_code}) - {emp.role}
                      </option>
                    ))}
                </select>
              </div>

              {/* Working Hours Section */}
              <div className="card bg-light border-0 p-3 mb-3">
                <h3 className="fs-6 mb-3">Working Hours</h3>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Check-in Time</label>
                    <input
                      type="time"
                      name="working_hours.check_in_time"
                      className="form-control"
                      value={formData.working_hours.check_in_time}
                      onChange={handleChange}
                    />
                    <small className="form-text text-muted">
                      Expected check-in time (24-hour format)
                    </small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Check-out Time</label>
                    <input
                      type="time"
                      name="working_hours.check_out_time"
                      className="form-control"
                      value={formData.working_hours.check_out_time}
                      onChange={handleChange}
                    />
                    <small className="form-text text-muted">
                      Expected check-out time (24-hour format)
                    </small>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="form-label">Timezone</label>
                  <select
                    name="working_hours.timezone"
                    className="form-select"
                    value={formData.working_hours.timezone}
                    onChange={handleChange}
                  >
                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                    <option value="Europe/London">Europe/London (GMT)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                    <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                  </select>
                  <small className="form-text text-muted">
                    Timezone for attendance tracking
                  </small>
                </div>
              </div>

              <div className="form-check form-switch mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  id="isActiveCheck"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="isActiveCheck">Active</label>
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/admin/employees')}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={updating}>
                  {updating ? 'Updating...' : 'Update Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmployeeEdit;
